/**
 * PublicForm — the PUBLIC patient-facing texted form at /f/:token.
 *
 * Texted to contacts by recruiters ("please fill out this quick form"). No
 * auth, no CRM shell — the token (an unguessable per-link uuid with a ~14-day
 * expiry) is the only credential. Uses plain fetch (not the axios client) so
 * no auth headers, session refresh, or localStorage context ever touch this
 * page (the BookCallback pattern).
 *
 * One question per screen with a progress bar; answers autosave per question
 * (upsert server-side, so re-answering is harmless). The server never returns
 * answer VALUES — resume state lives in localStorage (24h, WCR parity) plus
 * the server's answered-keys list; a show_if whose referenced answer is
 * unknown stays hidden, exactly like the old site treated unanswered refs.
 * The thank-you screen is outcome-blind: eligibility is recruiter-only.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import FormRenderer from '../components/forms/FormRenderer';
import QuestionnaireForm from '../components/forms/QuestionnaireForm';
import {
  API_URL,
  PublicQuestion,
  hasValue,
  isVisible,
  loadLocal,
  saveLocal,
} from '../components/forms/publicFormShared';

const AUTO_ADVANCE_MS = 400;

interface FormState {
  first_name?: string | null;
  form_title?: string;
  /** 'questionnaire' (the texted study form) renders as one scrollable page;
   * 'landing' keeps the one-question-per-screen stepper below. */
  surface?: string;
  completed?: boolean;
  greeting?: { message: string | null; bullets: string[] } | null;
  questions?: PublicQuestion[];
  answered_keys?: string[];
}

type Phase = 'loading' | 'invalid' | 'expired' | 'greeting' | 'fill' | 'done' | 'error';

export default function PublicForm() {
  const { token } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<Phase>('loading');
  const [data, setData] = useState<FormState>({});
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [idx, setIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [doneMessage, setDoneMessage] = useState<string | null>(null);
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Refs beat stale closures: the auto-advance timer must always act on the
  // LATEST answer and the LIVE saving flag — a state-captured closure would
  // silently drop a correction tapped during an in-flight save.
  const answersRef = useRef<Record<string, any>>({});
  const savingRef = useRef(false);
  // Keys confirmed persisted server-side (seeded from answered_keys, updated
  // per save) — clearing an optional answer must reach the server too.
  const savedKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => () => {
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
  }, []);
  // A pending auto-advance must never act on a question that already advanced.
  useEffect(() => {
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
  }, [idx]);

  const load = useCallback(async () => {
    setPhase('loading');
    try {
      const res = await fetch(`${API_URL}/api/crm/public/forms/${token}`);
      if (res.status === 404) {
        const body = await res.json().catch(() => ({}));
        setPhase(body?.error === 'link_expired' ? 'expired' : 'invalid');
        return;
      }
      if (!res.ok) {
        setPhase('error');
        return;
      }
      const body: FormState = await res.json();
      setData(body);
      if (body.completed) {
        setPhase('done');
        return;
      }
      const local = token ? loadLocal(token) : {};
      const answered = new Set(body.answered_keys || []);
      setAnswers(local);
      answersRef.current = local;
      savedKeysRef.current = new Set(answered);

      // Resume at the first VISIBLE question with no local value and no
      // server-side answer; the greeting shows only on a fresh start. idx must
      // always land on a visible question — questions[idx] renders unguarded.
      const qs = body.questions || [];
      let target = -1;
      let any = false;
      for (let i = 0; i < qs.length; i++) {
        const q = qs[i];
        if (!isVisible(q, local)) continue;
        if (hasValue(local[q.key]) || answered.has(q.key)) {
          any = true;
          continue;
        }
        target = i;
        break;
      }
      if (target < 0) {
        // Everything visible is answered but the form was never submitted —
        // land on the LAST visible question so the patient can hit Submit.
        for (let i = qs.length - 1; i >= 0; i--) {
          if (isVisible(qs[i], local)) { target = i; break; }
        }
      }
      setIdx(Math.max(0, target));
      setPhase(!any && body.greeting ? 'greeting' : 'fill');
    } catch {
      setPhase('error');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const questions = useMemo(() => data.questions || [], [data.questions]);
  const visible = useMemo(
    () => questions.filter((q) => isVisible(q, answers)),
    [questions, answers]
  );
  const current = questions[idx];
  const currentVisibleIdx = current ? visible.findIndex((q) => q.key === current.key) : -1;
  const progress = visible.length > 0 ? Math.min(100, Math.round(((currentVisibleIdx < 0 ? 0 : currentVisibleIdx) / visible.length) * 100)) : 0;

  /** Next question index after `from`, honoring show_if with live answers. */
  const nextIdx = useCallback((from: number, values: Record<string, any>) => {
    for (let i = from + 1; i < questions.length; i++) {
      if (isVisible(questions[i], values)) return i;
    }
    return -1;
  }, [questions]);

  const prevIdx = useCallback((from: number, values: Record<string, any>) => {
    for (let i = from - 1; i >= 0; i--) {
      if (isVisible(questions[i], values)) return i;
    }
    return -1;
  }, [questions]);

  /** POST one answer (value: null clears it server-side). */
  const saveAnswer = useCallback(async (q: PublicQuestion, value: any): Promise<boolean> => {
    try {
      const res = await fetch(`${API_URL}/api/crm/public/forms/${token}/answers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question_key: q.key, value }),
      });
      if (res.ok) {
        if (value === null || value === undefined) savedKeysRef.current.delete(q.key);
        else savedKeysRef.current.add(q.key);
        return true;
      }
      const body = await res.json().catch(() => ({}));
      if (res.status === 409 && body?.error === 'already_completed') {
        setPhase('done');
        return false;
      }
      if (res.status === 404) {
        setPhase(body?.error === 'link_expired' ? 'expired' : 'invalid');
        return false;
      }
      setNotice(body?.detail || 'That answer could not be saved — please try again.');
      return false;
    } catch {
      setNotice('Connection problem — please check your signal and try again.');
      return false;
    }
  }, [token]);

  const complete = useCallback(async () => {
    setSaving(true);
    savingRef.current = true;
    setNotice(null);
    try {
      const res = await fetch(`${API_URL}/api/crm/public/forms/${token}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setDoneMessage(body?.completion_message || null);
        setPhase('done');
        if (token) {
          try { window.localStorage.removeItem(`crm-f-${token}`); } catch { /* noop */ }
        }
        return;
      }
      if (res.status === 400 && body?.error === 'missing_required' && Array.isArray(body.keys) && body.keys.length > 0) {
        const target = questions.findIndex((q) => q.key === body.keys[0]);
        if (target >= 0) setIdx(target);
        setNotice('A few required questions still need an answer.');
        return;
      }
      if (res.status === 404) {
        setPhase(body?.error === 'link_expired' ? 'expired' : 'invalid');
        return;
      }
      setNotice('Something went wrong submitting your answers. Please try again.');
    } catch {
      setNotice('Connection problem — please check your signal and try again.');
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  }, [token, questions]);

  /** Persist the current answer (read LIVE from answersRef) and advance (or
   * finish). If a save is already in flight, retry shortly instead of
   * dropping the action — a re-tapped correction must never be lost. */
  const advance = useCallback(async (q: PublicQuestion) => {
    if (savingRef.current) {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
      advanceTimer.current = setTimeout(() => { advanceRef.current?.(q); }, 150);
      return;
    }
    const values = answersRef.current;
    const value = values[q.key];
    if (!hasValue(value)) {
      const serverHas = savedKeysRef.current.has(q.key);
      // null = explicitly cleared THIS session; undefined = never loaded here
      // (cross-device resume: the server may hold the answer, values don't
      // travel — see answered_keys).
      const locallyCleared = value === null;
      if (q.required && (!serverHas || locallyCleared)) {
        setNotice('This question needs an answer before continuing.');
        return;
      }
      if (serverHas && locallyCleared) {
        // Clear the previously-saved optional answer so the stored answers
        // match what the patient last saw.
        setSaving(true);
        savingRef.current = true;
        const ok = await saveAnswer(q, null);
        setSaving(false);
        savingRef.current = false;
        if (!ok) return;
      }
      const ni = nextIdx(questions.indexOf(q), values);
      if (ni < 0) await complete();
      else setIdx(ni);
      return;
    }
    setSaving(true);
    savingRef.current = true;
    setNotice(null);
    const ok = await saveAnswer(q, value);
    setSaving(false);
    savingRef.current = false;
    if (!ok) return;
    const ni = nextIdx(questions.indexOf(q), answersRef.current);
    if (ni < 0) await complete();
    else setIdx(ni);
  }, [saveAnswer, nextIdx, questions, complete]);
  const advanceRef = useRef<typeof advance | null>(null);
  advanceRef.current = advance;

  const onAnswer = useCallback((key: string, value: any) => {
    if (!current || current.key !== key) return;
    const next = { ...answersRef.current, [key]: value };
    answersRef.current = next;
    setAnswers(next);
    if (token) saveLocal(token, next);
    setNotice(null);
    // WCR parity: tap-to-answer types auto-advance after a beat.
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    if ((current.type === 'single_choice' || current.type === 'yes_no') && hasValue(value)) {
      const q = current;
      advanceTimer.current = setTimeout(() => { advanceRef.current?.(q); }, AUTO_ADVANCE_MS);
    }
  }, [current, token]);

  const greetingName = data.first_name ? `Hi ${data.first_name}!` : 'Hi there!';
  const isLast = current ? nextIdx(idx, answers) < 0 : true;
  const canGoBack = current ? prevIdx(idx, answers) >= 0 : false;
  // Study questionnaires render as one scrollable page; landing forms keep the
  // one-question-per-screen stepper below.
  const isFullForm = data.surface === 'questionnaire';

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 dark:bg-gray-900">
      <div className="mx-auto w-full max-w-md">
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-600 text-xl font-bold text-white">W</div>
          <h1 className="mt-3 text-lg font-semibold text-gray-900 dark:text-gray-100">West Clinical Research</h1>
        </div>

        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          {phase === 'loading' && (
            <div className="flex items-center justify-center py-12">
              <div className="h-7 w-7 animate-spin rounded-full border-b-2 border-primary-600" />
            </div>
          )}

          {(phase === 'invalid' || phase === 'expired') && (
            <div className="py-6 text-center">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {phase === 'expired' ? 'This link has expired' : 'This link is no longer valid'}
              </h2>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                No problem — just reply to our text message and we'll send you a fresh link, or we'll give you a call.
              </p>
            </div>
          )}

          {phase === 'error' && (
            <div className="py-6 text-center">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">This form is temporarily unavailable</h2>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Please try again in a few minutes.</p>
              <button
                onClick={load}
                className="mt-4 min-h-[44px] rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
              >
                Try again
              </button>
            </div>
          )}

          {phase === 'done' && (
            <div className="py-6 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-2xl dark:bg-green-900/30">✓</div>
              <h2 className="mt-3 text-base font-semibold text-gray-900 dark:text-gray-100">All done — thank you!</h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {doneMessage || "We've received your answers. Our team will review them and reach out about next steps."}
              </p>
            </div>
          )}

          {phase === 'greeting' && (
            <div className="py-2">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{greetingName}</h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {data.greeting?.message || `A few quick questions about ${data.form_title || 'the study'} — it only takes a couple of minutes.`}
              </p>
              {(data.greeting?.bullets || []).length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {data.greeting!.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <span className="mt-0.5 text-primary-600 dark:text-primary-400">✓</span>
                      {b}
                    </li>
                  ))}
                </ul>
              )}
              <button
                onClick={() => setPhase('fill')}
                className="mt-5 w-full rounded-lg bg-primary-600 px-4 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Get started
              </button>
            </div>
          )}

          {/* Study questionnaire: every question on one scrollable page. */}
          {phase === 'fill' && isFullForm && (
            <QuestionnaireForm
              token={token!}
              formTitle={data.form_title}
              questions={questions}
              initialAnswers={answers}
              initialAnsweredKeys={data.answered_keys || []}
              onCompleted={(msg) => { setDoneMessage(msg); setPhase('done'); }}
              onExpired={() => setPhase('expired')}
              onInvalid={() => setPhase('invalid')}
            />
          )}

          {/* Landing form: one question per screen with auto-advance. */}
          {phase === 'fill' && !isFullForm && current && (
            <div>
              <div className="mb-4">
                <div className="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
                  <span>{data.form_title}</span>
                  <span>
                    {Math.max(1, currentVisibleIdx + 1)} of {visible.length}
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                  <div
                    className="h-full rounded-full bg-primary-600 transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              {notice && (
                <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                  {notice}
                </div>
              )}

              <FormRenderer
                mode="single"
                questions={[{
                  key: current.key,
                  label: current.label,
                  type: current.type,
                  options: current.options,
                  required: current.required,
                  helpText: current.description,
                  placeholder: current.placeholder,
                  min: current.min,
                  max: current.max,
                }]}
                answers={{ [current.key]: answers[current.key] }}
                onAnswer={onAnswer}
                readOnly={saving}
              />

              <div className="mt-5 flex items-center gap-2">
                {canGoBack && (
                  <button
                    onClick={() => {
                      if (advanceTimer.current) clearTimeout(advanceTimer.current);
                      const pi = prevIdx(idx, answers);
                      if (pi >= 0) setIdx(pi);
                    }}
                    disabled={saving}
                    className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
                  >
                    Back
                  </button>
                )}
                <button
                  onClick={() => {
                    if (advanceTimer.current) clearTimeout(advanceTimer.current);
                    advance(current);
                  }}
                  disabled={saving || (current.required
                    && !hasValue(answers[current.key])
                    // Server-answered on another device counts (values never
                    // travel; the server's missing_required is authoritative).
                    && !(savedKeysRef.current.has(current.key) && answers[current.key] !== null))}
                  className="min-h-[44px] flex-1 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
                >
                  {saving ? 'Saving…' : isLast ? 'Submit' : 'Next'}
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-gray-400">
          Your answers are sent securely to our team. Questions? Reply to our text message any time.
        </p>
      </div>
    </div>
  );
}
